import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import BreadcrumbTrail from '../../components/ui/BreadcrumbTrail';
import ServiceCard from './components/ServiceCard';
import ServiceHours from './components/ServiceHours';
import ServiceFAQ from './components/ServiceFAQ';
import ServiceStatus from './components/ServiceStatus';
import { useVisitorTracking } from '../../hooks/useVisitorTracking';

const PublicServicesPortal = () => {
  // Initialize visitor tracking
  useVisitorTracking();

  // Check for saved language preference
  useEffect(() => {
    const savedLanguage = localStorage.getItem('preferred-language') || 'th';
    // In a real app, this would update the language context
    console.log('Current language:', savedLanguage);
  }, []);

  const services = [
    {
      id: 1,
      title: 'ศูนย์ดำรงธรรมอำเภอเทิง',
      description: `ศูนย์ดำรงธรรมอำเภอเทิง ให้บริการรับเรื่องร้องเรียนและข้อเสนอแนะจากประชาชน\nเพื่อส่งเสริมการมีส่วนร่วมของประชาชนในการพัฒนาท้องถิ่น`,
      icon: 'Damrongtham',
      status: 'available',
      processingTime: '3-7 วันทำการ',
      requirements: 'บัตรประชาชน, หลักฐานประกอบ (ถ้ามี)',
      fee: 'ไม่มีค่าธรรมเนียม',
      actionText: 'ยื่นเรื่องร้องเรียน',
      actionLink: '/complaint-submission',
      infoLink: '/damrongtham-info'
    },
    {
      id: 5,
      title: 'คู่มือประชาชน',
      description: `รวมขั้นตอน เอกสาร และแบบฟอร์มสำหรับการขอรับบริการต่างๆ ของภาครัฐ`,
      icon: 'Book',
      status: 'available',
      processingTime: 'แตกต่างกันตามบริการ',
      requirements: 'ตามที่ระบุในคู่มือ',
      fee: '-',
      actionText: 'เปิดดูคู่มือ',
      actionLink: '/citizen-guides',
      infoLink: '/citizen-guides'
    },
    {
      id: 2,
      title: 'ขอหนังสือรับรองต่างๆ',
      description: `บริการออกหนังสือรับรองการอยู่อาศัย หนังสือรับรองรายได้ และเอกสารราชการอื่นๆ\nสำหรับประชาชนในเขตพื้นที่รับผิดชอบ`,
      icon: 'FileText',
      status: 'available',
      processingTime: '1-3 วันทำการ',
      requirements: 'บัตรประชาชน, ทะเบียนบ้าน, หลักฐานการอยู่อาศัย',
      fee: '20-50 บาท (ตามประเภทเอกสาร)',
      actionText: 'ดูข้อมูลเพิ่มเติม',
      actionLink: null,
      infoLink: '/certificate-info'
    },
    {
      id: 3,
      title: 'ลงทะเบียนผู้ประกอบการ',
      description: `บริการลงทะเบียนและออกใบอนุญาตสำหรับผู้ประกอบการในพื้นที่\nรวมถึงการต่ออายุและแก้ไขข้อมูลใบอนุญาต`,
      icon: 'Building',
      status: 'limited',
      processingTime: '7-14 วันทำการ',
      requirements: 'เอกสารประกอบการ, แผนผังสถานที่, บัตรประชาชน',
      fee: '100-500 บาท (ตามประเภทกิจการ)',
      actionText: 'ดูข้อมูลเพิ่มเติม',
      actionLink: null,
      infoLink: '/business-registration-info'
    },
    {
      id: 4,
      title: 'บริการข้อมูลข่าวสาร',
      description: `ขอข้อมูลข่าวสารของราชการ ตามพระราชบัญญัติข้อมูลข่าวสารของราชการ พ.ศ. 2540\nและการเข้าถึงข้อมูลสาธารณะ`,
      icon: 'Search',
      status: 'available',
      processingTime: '15-30 วันทำการ',
      requirements: 'คำร้องขอข้อมูล, บัตรประชาชน',
      fee: 'ตามอัตราที่กำหนด',
      actionText: 'ดูข้อมูลเพิ่มเติม',
      actionLink: null,
      infoLink: '/information-request-info'
    },
    {
      id: 6,
      title: 'จองคิวงานขออนุญาตฝ่ายปกครอง',
      description: `ระบบจองคิวออนไลน์สำหรับการขออนุญาตต่างๆ เช่น อาวุธปืน โรงแรม การพนัน\nเพื่อความสะดวกและลดเวลารอคอย`,
      icon: 'Calendar',
      status: 'available',
      processingTime: 'ตามเวลาที่จอง',
      requirements: 'บัตรประชาชน, เบอร์โทรศัพท์',
      fee: 'ไม่มีค่าธรรมเนียม',
      actionText: 'จองคิว',
      actionLink: '/appointment-calendar',
      infoLink: '/appointment-info'
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>บริการประชาชน - อำเภอเทิง</title>
        <meta name="description" content="เข้าถึงบริการต่างๆ ของอำเภอเทิง รวมถึงระบบร้องเรียนออนไลน์ การขอหนังสือรับรอง และบริการอื่นๆ สำหรับประชาชน" />
        <meta name="keywords" content="บริการประชาชน, ร้องเรียน, หนังสือรับรอง, อำเภอเทิง, ราชการ" />
      </Helmet>
      <Header />
      <main className="max-w-7xl mx-auto px-4 lg:px-6 py-6">
        <BreadcrumbTrail />
        
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-primary-foreground" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2L2 7v10c0 5.55 3.84 9.74 9 11 5.16-1.26 9-5.45 9-11V7l-10-5z"/>
                <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" fill="none"/>
              </svg>
            </div>
            <div>
              <h1 className="text-3xl font-bold text-foreground">
                บริการประชาชน
              </h1>
              <p className="text-muted-foreground mt-1">
                เข้าถึงบริการต่างๆ ของอำเภอเทิงอย่างสะดวกและรวดเร็ว
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Services Section */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-card border border-border rounded-lg p-6 shadow-government">
              <div className="flex items-center space-x-2 mb-6">
                <svg className="w-5 h-5 text-primary" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
                </svg>
                <h2 className="text-xl font-semibold text-foreground">
                  บริการหลัก
                </h2>
              </div>
              
              <div className="grid grid-cols-1 gap-6">
                {services?.map((service) => (
                  <ServiceCard key={service?.id} service={service} />
                ))}
              </div>

            </div>

            {/* Service Status */}
            <ServiceStatus />
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Service Hours */}
            <ServiceHours />
            
            {/* FAQ */}
            <ServiceFAQ />
          </div>
        </div>

        {/* Additional Information */}
        <div className="mt-8 bg-muted/30 rounded-lg p-8">
          <div className="text-center max-w-3xl mx-auto">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-primary" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-foreground mb-4">
              พร้อมให้บริการประชาชน
            </h3>
            <p className="text-muted-foreground leading-relaxed mb-6">
              ที่ทำการปกครองอำเภอเทิงมุ่งมั่นที่จะให้บริการประชาชนด้วยความรวดเร็ว โปร่งใส และเป็นธรรม 
              ตามหลักการบริหารจัดการที่ดี เพื่อความพึงพอใจของประชาชนเป็นสำคัญ
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                <span>บริการรวดเร็ว</span>
              </div>
              <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                  <path d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                </svg>
                <span>โปร่งใส</span>
              </div>
              <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3"/>
                </svg>
                <span>เป็นธรรม</span>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PublicServicesPortal;